// DashboardContentBuilder.jsx
import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Card, Row, Col, Badge, InputGroup } from "react-bootstrap";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
//Components
import Navbar from '../NavBar/Navbar';
//Alerts 
import ErrorAlert from "../Alerts/ErrorAlert.jsx";
import SuccessAlert from "../Alerts/SuccessAlert";
import LoadingAlert from "../Alerts/LoadingAlert.jsx";
//API
import { GroupsFechtAPI } from '../../api/Groups';
import { CategoriesDashFetchAPI } from '../../api/CategoriesDash';
import { BranchOfficeFetchAPI } from '../../api/BranchOffices';
import { EcommerceManagerFetchAPI } from "../../api/ecommers/Manager.Content.jsx";

//Utils 
import { RenderIf } from "../utils/RenderIf.jsx";
const MAX_VIDEO_MB = 50;
const clamp = (str = "", max) => (str.length > max ? str.slice(0, max) : str);

// Detecta si la URL parece video simple (ajusta si soportas otros formatos)
const isVideoUrl = (url) => /\.mp4($|\?)/i.test(url || "");

// Mapea head del backend -> estado local (acepta "og_title" o "og:title")
function mapHeadFromServer(head) {
    const s = head?.social || {};
    return {
        title: head?.title || "Smart eCommerce | Tecnología y Accesorios en Panamá",
        meta: {
            description:
                head?.meta?.description ||
                "Compra tecnología, gadgets y accesorios con entregas rápidas en Panamá. Ofertas, garantía local y soporte técnico.",
        },
        icon: {
            file: null,                             // <— para nuevos uploads
            existingUrl: head?.icon?.url || "",     // <— URL existente (si ya hay)
            public_id: head?.icon?.public_id || "",
        },
        social: {
            og_title: s["og:title"] ?? s.og_title ?? "Smart eCommerce | Tecnología y Accesorios en Panamá",
            og_description: s["og:description"] ?? s.og_description ?? "Compra tecnología con entregas rápidas en Panamá. Ofertas y garantía local.",
            og_site_name: s["og:site_name"] ?? s.og_site_name ?? "Smart eCommerce",
            og_locale: s["og:locale"] ?? s.og_locale ?? "es_PA",
        },
    };
}


// Mapea hero_section del backend -> estado { type, media:[{file, existingUrl, title, subtitle}] }
function mapHeroFromServer(hero) {
    if (!hero || !hero.media) return { type: "image", media: [{ file: null, existingUrl: "", title: "", subtitle: "" }] };
    return {
        type: hero.type || "image",
        media: hero.media.map((m) => ({
            file: null,
            existingUrl: m.url || "",
            title: m.title || "",
            public_id: m.public_id || "",
            subtitle: m.subtitle || "",
            // opcional: poster si luego lo agregas
        })),
    };
}

function mapBenefitsFromServer(arr = []) {
    if (!Array.isArray(arr) || arr.length === 0) return [];
    return arr.map((c) => ({
        file: null,
        existingUrl: c.url || "",
        public_id: c.public_id || "",

        title: c.title || "",
        description: c.description || "",
    }));
}

function mapBannersFromServer(arr = []) {
    if (!Array.isArray(arr) || arr.length === 0) return [];
    return arr.map((b, i) => ({
        file: null,
        existingUrl: b.url || "",
        title: b.title || "",
        description: b.description || "",
        type: b.type || "left",
        position: Number(b.position ?? i + 1),
        cta_text: b.button?.text || "",
        cta_url: b.button?.href || "",
        public_id: b.public_id || "",

        _isVideoExisting: isVideoUrl(b.url || ""),
    }));
}


// ——— File input con preview (imagen o video) y validación tamaño ———
function FileInputWithPreview({ value, onChange, accept, maxSizeMB, label = "Archivo", existingUrl }) {
    const [previewUrl, setPreviewUrl] = useState(existingUrl || "");
    const [error, setError] = useState("");

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        setError("");
        if (!file) return;
        if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
            setError(`El archivo supera ${maxSizeMB} MB`);
            e.target.value = "";
            onChange(null);
            setPreviewUrl(existingUrl || "");
            return;
        }
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        onChange(file);
    };

    // refrescar preview si llega un File nuevo o cambia la URL existente
    React.useEffect(() => {
        if (value instanceof File) {
            const url = URL.createObjectURL(value);
            setPreviewUrl(url);
        } else if (!value && existingUrl) {
            setPreviewUrl(existingUrl);
        }
    }, [value, existingUrl]);

    const isImage = accept?.includes("image");
    const isVideo = accept?.includes("video");

    return (
        <div>
            <Form.Label>{label}</Form.Label>
            <Form.Control type="file" accept={accept} onChange={handleFileChange} />
            {error && <div className="text-danger mt-1 small">{error}</div>}
            {previewUrl && (
                <div className="mt-2">
                    {isImage && !previewUrl.endsWith(".mp4") && (
                        <img
                            src={previewUrl}
                            alt="preview"
                            style={{ maxWidth: "100%", maxHeight: 160, objectFit: "contain", borderRadius: 6 }}
                        />
                    )}
                    {isVideo || previewUrl.endsWith(".mp4") ? (
                        <video src={previewUrl} controls style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 6 }} />
                    ) : null}
                </div>
            )}
        </div>
    );
}


// ——— Modal: HERO ———
function HeroModal({ value, onChange }) {
    const [show, setShow] = useState(false);
    const [type, setType] = useState(value?.type || "image");
    const [media, setMedia] = useState(
        value?.media?.length ? value.media : [{ file: null, title: "", subtitle: "" }]
    );

    const addMedia = () =>
        setMedia((m) => [...m, { file: null, title: "", subtitle: "" }]);

    const removeMedia = (idx) => setMedia((m) => m.filter((_, i) => i !== idx));

    const updateMedia = (idx, field, v) =>
        setMedia((m) => m.map((it, i) => (i === idx ? { ...it, [field]: v } : it)));

    const canAdd = type === "video" ? media.length < 1 : true;

    const handleSave = () => {
        if (type === "video") {
            if (media.length !== 1) return ErrorAlert("Para 'video' solo se permite un archivo.", "Advertencia!", "warning");
            const f = media[0]?.file;
            const hasExisting = !!media[0]?.existingUrl;
            if (!(f instanceof File) && !hasExisting) return ErrorAlert("Selecciona un video.", "Advertencia!", "warning");
            if (f instanceof File && f.size > MAX_VIDEO_MB * 1024 * 1024) {
                return ErrorAlert(`El video supera ${MAX_VIDEO_MB}MB.`, "Advertencia!", "warning");
            }
        } else {
            if (!media.length) return ErrorAlert("Agrega al menos una imagen.", "Advertencia!", "warning");
            const invalid = media.some((m) => !(m.file instanceof File) && !m.existingUrl);
            if (invalid) return ErrorAlert("Todas las imágenes deben tener archivo o URL existente.", "Advertencia!", "warning");
        }

        onChange({
            type,
            media: media.map((m) => ({
                file: m.file || null,
                existingUrl: m.existingUrl || "",
                title: m.title?.trim() || "",
                subtitle: m.subtitle?.trim() || "",
            })),
        });
        setShow(false);
    };

    // función para copiar desde props.value
    const hydrateFromValue = React.useCallback(() => {
        setType(value?.type || "image");
        setMedia(
            value?.media?.length
                ? value.media.map(m => ({
                    file: m.file || null,
                    existingUrl: m.existingUrl || m.url || "",
                    title: m.title || "",
                    subtitle: m.subtitle || ""
                }))
                : [{ file: null, existingUrl: "", title: "", subtitle: "" }]
        );
    }, [value]);

    // si value cambia desde el padre, actualiza estado interno
    React.useEffect(() => {
        if (!show) hydrateFromValue();
    }, [value, show, hydrateFromValue]);

    // al abrir, rehidrata y luego muestra
    const open = () => { hydrateFromValue(); setShow(true); };

    return (
        <>
            <Button variant="outline-primary" onClick={open}>
                Configurar Hero Section
            </Button>
            <Modal show={show} onHide={() => setShow(false)} size="lg" centered>
                <Modal.Header closeButton><Modal.Title>Hero Section</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Tipo</Form.Label>
                        <Form.Select
                            value={type}
                            onChange={(e) => {
                                const next = e.target.value;
                                setType(next);
                                if (next === "video" && media.length > 1) {
                                    setMedia([media[0]]);
                                }
                            }}
                        >
                            <option value="image">image</option>
                            <option value="video">video</option>
                        </Form.Select>
                        {type === "video" && (
                            <Badge bg="secondary" className="ms-2">Límite: {MAX_VIDEO_MB}MB</Badge>
                        )}
                    </Form.Group>

                    {media.map((m, idx) => (
                        <Card className="p-3 mb-3" key={`hero-${idx}`}>
                            <Row className="g-3">
                                <Col md={7}>
                                    <FileInputWithPreview
                                        value={m.file}
                                        existingUrl={m.existingUrl}
                                        onChange={(file) => updateMedia(idx, "file", file)}
                                        accept={type === "video" ? "video/*" : "image/*"}
                                        maxSizeMB={type === "video" ? MAX_VIDEO_MB : undefined}
                                        label={type === "video" ? "Video" : "Imagen"}
                                    />
                                </Col>
                                <Col md={5}>
                                    <Form.Label>Título</Form.Label>
                                    <Form.Control
                                        value={m.title}
                                        onChange={(e) => updateMedia(idx, "title", e.target.value)}
                                    />
                                    <Form.Label className="mt-2">Subtítulo</Form.Label>
                                    <Form.Control
                                        value={m.subtitle}
                                        onChange={(e) => updateMedia(idx, "subtitle", e.target.value)}
                                    />
                                </Col>
                            </Row>
                            <div className="d-flex justify-content-end mt-2">
                                <Button
                                    variant="outline-danger"
                                    onClick={() => removeMedia(idx)}
                                    disabled={media.length === 1}
                                >
                                    Eliminar
                                </Button>
                            </div>
                        </Card>
                    ))}

                    <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={addMedia}
                        disabled={!canAdd}
                    >
                        Agregar {type === "video" ? "video" : "imagen"}
                    </Button>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleSave}>Guardar</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

// ——— Modal: BENEFIT CARDS ———
function BenefitsModal({ value = [], onChange }) {
    const [show, setShow] = useState(false);
    const [cards, setCards] = useState(
        value.length ? value : [{ file: null, existingUrl: "", title: "", description: "" }, { file: null, existingUrl: "", title: "", description: "" }]
    );

    const addCard = () =>
        setCards((arr) => [...arr, { file: null, title: "", description: "" }]);
    const removeCard = (idx) => setCards((arr) => arr.filter((_, i) => i !== idx));
    const update = (idx, field, v) =>
        setCards((arr) => arr.map((c, i) => (i === idx ? { ...c, [field]: v } : c)));

    const handleSave = () => {

        if (cards.length > 4) {
            return ErrorAlert("Debe haber un maximo de 4 benefit cards.", "Advertencia!", "warning");
        }
        for (const c of cards) {
            const hasFile = c.file instanceof File;
            const hasExisting = !!c.existingUrl;
            if (!hasFile && !hasExisting) return ErrorAlert("Cada card debe tener imagen/ícono.", "Advertencia!", "warning");
            if (!c.title?.trim()) return ErrorAlert("Cada card debe tener título.", "Advertencia!", "warning");
            if (!c.description?.trim()) return ErrorAlert("Cada card debe tener descripción.", "Advertencia!", "warning");
            if (c.description.length > 200) return ErrorAlert("Descripción ≤ 200 caracteres.", "Advertencia!", "warning");
        }
        onChange(cards);
        setShow(false);
    };

    const hydrateFromValue = React.useCallback(() => {
        setCards(
            value.length
                ? value.map(c => ({
                    file: c.file || null,
                    existingUrl: c.existingUrl || c.url || "",
                    title: c.title || "",
                    description: c.description || ""
                }))
                : [{ file: null, existingUrl: "", title: "", description: "" }, { file: null, existingUrl: "", title: "", description: "" }]
        );
    }, [value]);

    const open = () => { hydrateFromValue(); setShow(true); };

    React.useEffect(() => {
        if (!show) hydrateFromValue();
    }, [value, show, hydrateFromValue]);

    React.useEffect(() => {
        if (!show) hydrateFromValue();
    }, [value, show, hydrateFromValue]);

    return (
        <>
            <Button variant="outline-success" onClick={open} className="ms-2">
                Configurar Benefit Cards
            </Button>
            <Modal show={show} onHide={() => setShow(false)} size="lg" centered>
                <Modal.Header closeButton><Modal.Title>Benefit Cards (2–4)</Modal.Title></Modal.Header>
                <Modal.Body>
                    {cards.map((c, idx) => (
                        <Card className="p-3 mb-3" key={`benefit-${idx}`}>
                            <Row className="g-3">
                                <Col md={4}>
                                    <FileInputWithPreview
                                        value={c.file}
                                        existingUrl={c.existingUrl}
                                        onChange={(f) => update(idx, "file", f)}
                                        accept="image/*"
                                        label="Imagen o ícono"
                                    />
                                </Col>
                                <Col md={8}>
                                    <Form.Label>Título</Form.Label>
                                    <Form.Control
                                        value={c.title}
                                        onChange={(e) => update(idx, "title", e.target.value)}
                                    />
                                    <Form.Label className="mt-2">
                                        Descripción <Badge bg="light" text="dark">{(c.description || "").length}/200</Badge>
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={c.description}
                                        onChange={(e) => update(idx, "description", clamp(e.target.value, 200))}
                                    />
                                </Col>
                            </Row>
                            <div className="d-flex justify-content-end mt-2">
                                <Button
                                    variant="outline-danger"
                                    onClick={() => removeCard(idx)}
                                >
                                    Eliminar
                                </Button>
                            </div>
                        </Card>
                    ))}
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={addCard}
                        disabled={cards.length >= 4}
                    >
                        Agregar Card
                    </Button>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleSave} disabled={cards.length > 4}>Guardar</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

// ——— Modal: BANNERS ———
function BannersModal({ value = [], onChange }) {
    const [show, setShow] = useState(false);
    const [items, setItems] = useState(
        value.length ? value : [{ file: null, existingUrl: "", title: "", description: "", type: "left", position: 1, cta_text: "", cta_url: "" }]
    );

    const hydrateFromValue = React.useCallback(() => {
        setItems(
            value.length
                ? value.map((b, i) => ({
                    file: b.file || null,
                    existingUrl: b.existingUrl || b.url || "",
                    title: b.title || "",
                    description: b.description || "",
                    type: b.type || "left",
                    position: Number(b.position ?? i + 1),
                    cta_text: b.cta_text || b.button?.text || "",
                    cta_url: b.cta_url || b.button?.href || ""
                }))
                : [{ file: null, existingUrl: "", title: "", description: "", type: "left", position: 1, cta_text: "", cta_url: "" }]
        );
    }, [value]);

    React.useEffect(() => {
        if (!show) hydrateFromValue();
    }, [value, show, hydrateFromValue]);

    const open = () => { hydrateFromValue(); setShow(true); };

    const add = () =>
        setItems((arr) => [
            ...arr,
            { file: null, title: "", description: "", type: "left", position: arr.length + 1, cta_text: "", cta_url: "" },
        ]);

    const remove = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
    const update = (idx, field, v) =>
        setItems((arr) => arr.map((c, i) => (i === idx ? { ...c, [field]: v } : c)));

    const handleSave = () => {
        for (const b of items) {
            const hasFile = b.file instanceof File;
            const hasExisting = !!b.existingUrl;
            if (!hasFile && !hasExisting) return ErrorAlert("Cada banner requiere imagen o video.", "Advertencia!", "warning");
            if (!b.title?.trim() || b.title.length > 100) return ErrorAlert("Título requerido (≤100).", "Advertencia!", "warning");
            if (!b.description?.trim() || b.description.length > 400) return ErrorAlert("Descripción requerida (≤400).", "Advertencia!", "warning");
            if (!["left", "right", "full"].includes(b.type)) return ErrorAlert("Tipo inválido.", "Advertencia!", "warning");
            if (!Number.isFinite(Number(b.position))) return ErrorAlert("Position debe ser numérica.", "Advertencia!", "warning");
        }
        onChange(items);
        setShow(false);
    };

    return (
        <>
            <Button variant="outline-warning" onClick={open} className="ms-2">
                Configurar Banners
            </Button>
            <Modal show={show} onHide={() => setShow(false)} size="lg" centered>
                <Modal.Header closeButton><Modal.Title>Banners</Modal.Title></Modal.Header>
                <Modal.Body>
                    {items.map((b, idx) => {
                        const isVideo = b.file instanceof File && b.file.type.startsWith("video/");
                        return (
                            <Card className="p-3 mb-3" key={`banner-${idx}`}>
                                <Row className="g-3">
                                    <Col md={5}>
                                        <FileInputWithPreview
                                            value={b.file}
                                            existingUrl={b.existingUrl}
                                            onChange={(f) => update(idx, "file", f)}
                                            accept="image/*,video/*"
                                            label="Imagen o Video"
                                        />
                                    </Col>
                                    <Col md={7}>
                                        <Form.Label>Título <Badge bg="light" text="dark">{(b.title || "").length}/100</Badge></Form.Label>
                                        <Form.Control
                                            value={b.title}
                                            onChange={(e) => update(idx, "title", clamp(e.target.value, 100))}
                                        />
                                        <Form.Label className="mt-2">
                                            Descripción <Badge bg="light" text="dark">{(b.description || "").length}/400</Badge>
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={b.description}
                                            onChange={(e) => update(idx, "description", clamp(e.target.value, 400))}
                                        />
                                        <Row className="mt-2">
                                            <Col md={6}>
                                                <Form.Label>Tipo</Form.Label>
                                                <Form.Select
                                                    value={b.type}
                                                    onChange={(e) => update(idx, "type", e.target.value)}
                                                >
                                                    <option value="left">left</option>
                                                    <option value="right">right</option>
                                                    <option value="full">full</option>
                                                </Form.Select>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Label>Position</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    value={b.position}
                                                    onChange={(e) => update(idx, "position", Number(e.target.value))}
                                                />
                                            </Col>
                                        </Row>
                                        <Row className="mt-2">
                                            <Col md={6}>
                                                <Form.Label>Texto del Botón</Form.Label>
                                                <Form.Control
                                                    value={b.cta_text}
                                                    onChange={(e) => update(idx, "cta_text", e.target.value)}
                                                    placeholder="Ver más / Comprar"
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Form.Label>URL de Redirección</Form.Label>
                                                <Form.Control
                                                    type="url"
                                                    value={b.cta_url}
                                                    onChange={(e) => update(idx, "cta_url", e.target.value)}
                                                    placeholder="https://..."
                                                />
                                            </Col>
                                        </Row>
                                        {isVideo && <Badge bg="secondary" className="mt-2">Se detectó video</Badge>}
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-end mt-2">
                                    <Button variant="outline-danger" onClick={() => remove(idx)}>
                                        Eliminar
                                    </Button>
                                </div>
                            </Card>
                        );
                    })}
                    <Button size="sm" variant="outline-secondary" onClick={add}>
                        Agregar Banner
                    </Button>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleSave}>Guardar</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

// ——— Modal: CONTACTO ———
const ECM_MAP_DEFAULT_CENTER = { lat: 8.9824, lng: -79.5199 }; // Panamá

function ContactModal({ value, onChange }) {
    const [show, setShow] = useState(false);
    const [state, setState] = useState(
        value || { direction: { name: "", lat: "", lng: "", additional_info: "", house_num: "" }, phone: "", email: "", open_hour: [{ days: "", hour: "" }], whatsapp_phone: "", whatsapp_message: "" }
    );

    const hydrateFromValue = React.useCallback(() => {
        setState(
            value || { direction: { name: "", lat: "", lng: "", additional_info: "", house_num: "" }, phone: "", email: "", open_hour: [{ days: "", hour: "" }], whatsapp_phone: "", whatsapp_message: "" }
        );
    }, [value]);

    React.useEffect(() => {
        if (!show) hydrateFromValue();
    }, [value, show, hydrateFromValue]);

    const open = () => { hydrateFromValue(); setShow(true); };



    const update = (path, v) => {
        setState((s) => {
            const clone = structuredClone(s);
            const keys = path.split(".");
            let cur = clone;
            for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
            cur[keys.at(-1)] = v;
            return clone;
        });
    };

    const setOH = (idx, field, v) => {
        const arr = [...state.open_hour];
        arr[idx] = { ...arr[idx], [field]: v };
        setState((s) => ({ ...s, open_hour: arr }));
    };
    const addOH = () => setState((s) => ({ ...s, open_hour: [...s.open_hour, { days: "", hour: "" }] }));
    const removeOH = (idx) => setState((s) => ({ ...s, open_hour: s.open_hour.filter((_, i) => i !== idx) }));

    const parsedLat = parseFloat(state.direction.lat);
    const parsedLng = parseFloat(state.direction.lng);
    const hasCoords = !Number.isNaN(parsedLat) && !Number.isNaN(parsedLng)
        && !(parsedLat === 0 && parsedLng === 0)
        && parsedLat >= -90 && parsedLat <= 90 && parsedLng >= -180 && parsedLng <= 180;
    const mapCenter = hasCoords ? { lat: parsedLat, lng: parsedLng } : ECM_MAP_DEFAULT_CENTER;
    const handleMapClick = (e) => {
        const coords = e?.detail?.latLng;
        if (!coords) return;
        update("direction.lat", String(coords.lat));
        update("direction.lng", String(coords.lng));
    };
    const handleMarkerDragEnd = (e) => {
        const pos = e?.latLng;
        if (!pos) return;
        const latVal = typeof pos.lat === "function" ? pos.lat() : pos.lat;
        const lngVal = typeof pos.lng === "function" ? pos.lng() : pos.lng;
        update("direction.lat", String(latVal));
        update("direction.lng", String(lngVal));
    };

    const handleSave = () => {
        if (!state.direction.name?.trim()) return ErrorAlert("Nombre de ubicación es requerido.", "Advertencia!", "warning");
        if (!state.phone?.trim()) return ErrorAlert("Teléfono es requerido.", "Advertencia!", "warning");
        if (!state.email?.trim()) return ErrorAlert("Email es requerido.", "Advertencia!", "warning");
        onChange(state);
        setShow(false);
    };

    return (
        <>
            <Button variant="outline-dark" onClick={open} className="ms-2">
                Configurar Contacto
            </Button>
            <Modal show={show} onHide={() => setShow(false)} size="lg" centered>
                <Modal.Header closeButton><Modal.Title>Contacto</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Card className="p-3 mb-3">
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Label>Nombre de ubicación</Form.Label>
                                <Form.Control value={state.direction.name} onChange={(e) => update("direction.name", e.target.value)} />
                            </Col>
                            <Col md={3}>
                                <Form.Label>Lat</Form.Label>
                                <Form.Control value={state.direction.lat} onChange={(e) => update("direction.lat", e.target.value)} />
                            </Col>
                            <Col md={3}>
                                <Form.Label>Lng</Form.Label>
                                <Form.Control value={state.direction.lng} onChange={(e) => update("direction.lng", e.target.value)} />
                            </Col>
                            <Col md={6}>
                                <Form.Label>Info adicional</Form.Label>
                                <Form.Control
                                    value={state.direction.additional_info}
                                    onChange={(e) => update("direction.additional_info", e.target.value)}
                                    placeholder="Por aseo capital, barriada de al frente"
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label>Número de casa</Form.Label>
                                <Form.Control
                                    value={state.direction.house_num}
                                    onChange={(e) => update("direction.house_num", e.target.value)}
                                    placeholder="Casa 64"
                                />
                            </Col>
                            <Col md={12}>
                                <Form.Label>Ubicación en el mapa (haz clic o arrastra el marcador)</Form.Label>
                                <div style={{ height: 320, borderRadius: 8, overflow: "hidden" }}>
                                    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                                        <Map
                                            key={hasCoords ? `${mapCenter.lat},${mapCenter.lng}` : "default"}
                                            defaultCenter={mapCenter}
                                            defaultZoom={hasCoords ? 16 : 12}
                                            style={{ width: "100%", height: "100%" }}
                                            mapTypeControl={false}
                                            streetViewControl={false}
                                            fullscreenControl={false}
                                            onClick={handleMapClick}
                                        >
                                            {hasCoords && (
                                                <Marker position={mapCenter} draggable onDragEnd={handleMarkerDragEnd} />
                                            )}
                                        </Map>
                                    </APIProvider>
                                </div>
                            </Col>
                        </Row>
                    </Card>

                    <Row className="g-3">
                        <Col md={4}>
                            <Form.Label>Teléfono</Form.Label>
                            <Form.Control value={state.phone} onChange={(e) => update("phone", e.target.value)} />
                        </Col>
                        <Col md={4}>
                            <Form.Label>Email</Form.Label>
                            <Form.Control type="email" value={state.email} onChange={(e) => update("email", e.target.value)} />
                        </Col>
                        <Col md={4}>
                            <Form.Label>WhatsApp</Form.Label>
                            <Form.Control value={state.whatsapp_phone} onChange={(e) => update("whatsapp_phone", e.target.value)} />
                        </Col>
                        <Col md={12}>
                            <Form.Label>Mensaje de whatsapp por defecto</Form.Label>
                            <Form.Control value={state.whatsapp_message} onChange={(e) => update("whatsapp_message", e.target.value)} />
                        </Col>
                    </Row>

                    <Card className="p-3 mt-3">
                        <div className="d-flex align-items-center justify-content-between">
                            <strong>Horarios de apertura</strong>
                            <Button size="sm" variant="outline-secondary" onClick={addOH}>Agregar</Button>
                        </div>
                        {state.open_hour.map((oh, idx) => (
                            <Row className="g-3 mt-1" key={`oh-${idx}`}>
                                <Col md={6}>
                                    <Form.Label>Días</Form.Label>
                                    <Form.Control value={oh.days} onChange={(e) => setOH(idx, "days", e.target.value)} placeholder="Lun–Vie" />
                                </Col>
                                <Col md={5}>
                                    <Form.Label>Horario</Form.Label>
                                    <Form.Control value={oh.hour} onChange={(e) => setOH(idx, "hour", e.target.value)} placeholder="9:00–18:00" />
                                </Col>
                                <Col md={1} className="d-flex align-items-end">
                                    <Button variant="outline-danger" onClick={() => removeOH(idx)} disabled={state.open_hour.length === 1}>X</Button>
                                </Col>
                            </Row>
                        ))}
                    </Card>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleSave}>Guardar</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}


//- Modal: Config 
//- Modal: Config 
function ConfigModal({ value, onChange }) {
    const [show, setShow] = useState(false);
    const defaults = {
        theme: "light", // light | dark | auto
        fontFamily: "Sans serif",
        googleFont: "", // eg. "Poppins"
        categories: {
            enabled: false,
            layout: "grid", // grid | list | carousel
            items: [] // [{id:'bebidas', label:'Bebidas'}]
        },
        recommended: {
            enabled: false,
            source: "bestsellers", // bestsellers | new | manual
            limit: 8,
            items: [],
            description: ""
        },
        delivery: {
            enabled: false,
        },
        pickup: {
            enabled: false,
        },
        reservations: {
            enabled: false,
        },
        events: {
            enabled: false,
            require_access_code: false,
            require_stand: false,
        },
        // Quotes module config
        quotes: {
            enabled: false,
            mode: "catalog", // "catalog" | "direct"
            allow_price_visibility: false, // show prices or hide them
            require_contact: true, // enforce contact fields before submit
            channels: { whatsapp: true, email: true } // available quote channels
        }
    };

    const [state, setState] = useState(value || defaults);

    const hydrate = React.useCallback(() => {
        setState({ ...defaults, ...(value || {}) });
    }, [value]);

    React.useEffect(() => {
        if (!show) hydrate();
    }, [value, show, hydrate]);

    const open = () => { hydrate(); setShow(true); };

    const update = (path, v) => {
        setState((s) => {
            const clone = structuredClone(s);
            const keys = path.split(".");
            let cur = clone;
            for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
            cur[keys.at(-1)] = v;
            return clone;
        });
    };

    const [branches, setBranches] = useState([]);
    const [branchId, setBranchId] = useState("");
    const [branchCats, setBranchCats] = useState([]);
    const [dragCat, setDragCat] = useState(null);
    React.useEffect(() => {
        if (!show) return;
        (async () => {
            try {
                const user = JSON.parse(localStorage.getItem("user"));
                const token = localStorage.getItem("x-access-token");
                const res = await BranchOfficeFetchAPI.getBranchOfficesByBusiness(user?.business_id ?? user?.business?.id, token);
                setBranches((res.data.data || []).map(b => ({ id: b.branchofficeId, name: b.branchofficeName })));
            } catch (err) {
                setBranches([]);
            }
        })();
    }, [show]);
    React.useEffect(() => {
        if (!show || !branchId) return setBranchCats([]);
        (async () => {
            try {
                const token = localStorage.getItem("x-access-token");
                const res = await CategoriesDashFetchAPI.getCategoriesByBranchOffice(branchId, token);
                setBranchCats((res.data.data || []).map(c => ({ id: c.category_id, label: c.category_name })));
            } catch (err) {
                setBranchCats([]);
            }
        })();
    }, [show, branchId]);
    //The order is saved right away for the selected branchoffice (same as in Productos > Categorías)
    const dropCat = async (to) => {
        if (dragCat === null || dragCat === to) return setDragCat(null);
        const previous = branchCats;
        const list = [...branchCats];
        const [moved] = list.splice(dragCat, 1);
        list.splice(to, 0, moved);
        setDragCat(null);
        setBranchCats(list);
        try {
            await CategoriesDashFetchAPI.sortCategoriesByBranchOffice(list.map(c => c.id), branchId, localStorage.getItem("x-access-token"));
            SuccessAlert("Orden actualizado");
        } catch (err) {
            setBranchCats(previous);
            ErrorAlert("", "Error, no se pudo guardar el orden", "error");
        }
    };
    const addCategory = () => {
        setState((s) => ({
            ...s,
            categories: {
                ...s.categories,
                items: [...s.categories.items, { id: "", label: "" }]
            }
        }));
    };
    const setCategory = (idx, field, v) => {
        setState((s) => {
            const items = [...s.categories.items];
            items[idx] = { ...items[idx], [field]: v };
            return { ...s, categories: { ...s.categories, items } };
        });
    };
    const rmCategory = (idx) => {
        setState((s) => {
            const items = s.categories.items.filter((_, i) => i !== idx);
            return { ...s, categories: { ...s.categories, items } };
        });
    };

    const [dragReco, setDragReco] = useState(null);
    const dropReco = (to) => {
        if (dragReco === null || dragReco === to) return setDragReco(null);
        setState((s) => {
            const items = [...s.recommended.items];
            const [moved] = items.splice(dragReco, 1);
            items.splice(to, 0, moved);
            return { ...s, recommended: { ...s.recommended, items } };
        });
        setDragReco(null);
    };
    const addReco = () => {
        setState((s) => ({
            ...s,
            recommended: {
                ...s.recommended,
                items: [...s.recommended.items, { sku: "", label: "" }]
            }
        }));
    };
    const setReco = (idx, field, v) => {
        setState((s) => {
            const items = [...s.recommended.items];
            items[idx] = { ...items[idx], [field]: v };
            return { ...s, recommended: { ...s.recommended, items } };
        });
    };
    const rmReco = (idx) => {
        setState((s) => {
            const items = s.recommended.items.filter((_, i) => i !== idx);
            return { ...s, recommended: { ...s.recommended, items } };
        });
    };

    const handleSave = () => {
        /*    if (state.recommended.enabled && state.recommended.source === "manual" && state.recommended.items.length === 0) {
               return ErrorAlert("Agrega al menos 1 ítem manual en Recomendados o cambia la fuente.", "Advertencia", "warning");
           } */
        /*  if (state.categories.enabled && state.categories.items.length === 0) {
             return ErrorAlert("Agrega al menos 1 categoría o deshabilita el módulo.", "Advertencia", "warning");
         } */
        const next = structuredClone(state);
        onChange(next);
        setShow(false);
    };

    return (
        <>
            <Button variant="outline-info" onClick={open} className="ms-2">
                Apariencia & Módulos
            </Button>

            <Modal show={show} onHide={() => setShow(false)} size="lg" centered>
                <Modal.Header closeButton><Modal.Title>Apariencia & Módulos</Modal.Title></Modal.Header>
                <Modal.Body>
                    {/* Tema */}
                    <Card className="p-3 mb-3">
                        <strong className="mb-2 d-block">Tema</strong>
                        <Row className="g-3">
                            <Col md={4}>
                                <Form.Label>Modo</Form.Label>
                                <Form.Select
                                    value={state.theme}
                                    onChange={(e) => update("theme", e.target.value)}
                                >
                                    <option value="light">Light</option>
                                    <option value="dark">Dark</option>
                                    <option value="auto">Auto (prefers-color-scheme)</option>
                                </Form.Select>
                            </Col>
                        </Row>
                    </Card>

                    {/* Fuente */}
                    <Card className="p-3 mb-3">
                        <strong className="mb-2 d-block">Fuente</strong>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Label>Google Font (opcional)</Form.Label>
                                <Form.Control
                                    placeholder='Ej: "Poppins", "Inter", "Montserrat"'
                                    value={state.googleFont}
                                    onChange={(e) => update("googleFont", e.target.value)}
                                />
                                <Form.Text>Si la dejas vacía, se usa la fuente del sistema.</Form.Text>
                            </Col>
                            <Col md={6}>
                                <Form.Label>Fallback / Personalizada</Form.Label>
                                <Form.Control
                                    value={state.fontFamily}
                                    onChange={(e) => update("fontFamily", e.target.value)}
                                />
                                <Form.Text>Puedes incluir la Google Font más fallbacks.</Form.Text>
                            </Col>
                        </Row>
                    </Card>

                    {/* Categorías */}
                    <Card className="p-3 mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <strong>Categorías</strong>
                            <Form.Check
                                type="switch"
                                id="categories-enabled"
                                label={state.categories.enabled ? "Habilitado" : "Deshabilitado"}
                                checked={state.categories.enabled}
                                onChange={(e) => update("categories.enabled", e.target.checked)}
                            />
                        </div>
                        <RenderIf isTrue={state.categories.enabled}>
                            <Row className="g-3 mt-1">
                                <Col md={4}>
                                    <Form.Label>Layout</Form.Label>
                                    <Form.Select
                                        value={state.categories.layout}
                                        onChange={(e) => update("categories.layout", e.target.value)}
                                    >
                                        <option value="grid">Grid</option>
                                        <option value="list">Lista</option>
                                        <option value="carousel">Carrusel</option>
                                    </Form.Select>
                                </Col>
                            </Row>
                            <div className="mt-3"><span className="fw-semibold">Orden de categorías por sucursal</span><div className="text-muted small">Elige una sucursal y arrastra las categorías para cambiar el orden en que se muestran en su ecommerce. Se guarda al soltar.</div></div><Form.Select className="mt-2" value={branchId} onChange={e => setBranchId(e.target.value)}><option value="">Selecciona una sucursal</option>{branches.map(b => (<option key={b.id} value={b.id}>{b.name}</option>))}</Form.Select>{branchId && branchCats.length === 0 && <div className="text-muted small mt-2">Esta sucursal no tiene categorías.</div>}{branchCats.map((c, idx) => (<div key={`cat-${c.id}`} draggable onDragStart={() => setDragCat(idx)} onDragOver={e => e.preventDefault()} onDrop={() => dropCat(idx)} className="d-flex align-items-center gap-2 border rounded-3 p-2 mt-2 bg-white" style={{ cursor: "grab", opacity: dragCat === idx ? 0.5 : 1 }}><span aria-hidden="true">⠿</span><span className="fw-semibold">{idx + 1}.</span><span>{c.label}</span></div>))}
                        </RenderIf>
                    </Card>

                    {/* Recomendados */}
                    <Card className="p-3 mb-2">
                        <div className="d-flex justify-content-between align-items-center">
                            <strong>Recomendados</strong>
                            <Form.Check
                                type="switch"
                                id="reco-enabled"
                                label={state.recommended.enabled ? "Habilitado" : "Deshabilitado"}
                                checked={state.recommended.enabled}
                                onChange={(e) => update("recommended.enabled", e.target.checked)}
                            />
                        </div>
                        <RenderIf isTrue={state.recommended.enabled}>
                            <Row className="g-3 mt-1">
                                <Col md={5}>
                                    <Form.Label>Fuente</Form.Label>
                                    <Form.Select
                                        value={state.recommended.source}
                                        onChange={(e) => update("recommended.source", e.target.value)}
                                    >
                                        <option value="bestsellers">Más vendidos</option>
                                        <option value="new">Novedades</option>
                                        <option value="manual">Manual</option>
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Límite</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={1}
                                        max={48}
                                        value={state.recommended.limit}
                                        onChange={(e) => update("recommended.limit", Number(e.target.value || 1))}
                                    />
                                </Col>
                                <Col>
                                    <Form.Label>Descripción</Form.Label>
                                    <Form.Control
                                        value={state.recommended.description}
                                        onChange={(e) => update("recommended.description", e.target.value)}
                                    />
                                </Col>
                            </Row>


                            <RenderIf isTrue={state.recommended.source === "manual"}>
                                <div className="d-flex align-items-center justify-content-between mt-3">
                                    <span className="fw-semibold">Ítems manuales</span>
                                    <Button size="sm" variant="outline-secondary" onClick={addReco}>Agregar</Button>
                                </div>
                                {state.recommended.items.map((r, idx) => (
                                    <Row className="g-2 mt-1" key={`reco-${idx}`} draggable onDragStart={() => setDragReco(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => dropReco(idx)} style={{ cursor: "grab", opacity: dragReco === idx ? 0.5 : 1 }} title="Arrastra para ordenar">
                                        <Col md={4}>
                                            <Form.Control
                                                placeholder="SKU / ID externo"
                                                value={r.sku}
                                                onChange={(e) => setReco(idx, "sku", e.target.value)}
                                            />
                                        </Col>
                                        <Col md={7}>
                                            <Form.Control
                                                placeholder="Etiqueta (opcional)"
                                                value={r.label}
                                                onChange={(e) => setReco(idx, "label", e.target.value)}
                                            />
                                        </Col>
                                        <Col md={1} className="d-flex align-items-end">
                                            <Button variant="outline-danger" onClick={() => rmReco(idx)}>X</Button>
                                        </Col>
                                    </Row>
                                ))}
                            </RenderIf>
                        </RenderIf>
                    </Card>
                    {/* Delivery & Pickup */}
                    <Card className="p-3 mb-2">
                        <strong className="mb-2 d-block">Opciones de compra</strong>
                        <Form.Check
                            type="switch"
                            id="delivery-enabled"
                            label={state.delivery.enabled ? "Delivery Habilitado" : "Delivery Deshabilitado"}
                            checked={state.delivery.enabled}
                            onChange={(e) => update("delivery.enabled", e.target.checked)}
                        />
                        <Form.Check
                            type="switch"
                            id="pickup-enabled"
                            label={state.pickup.enabled ? "Pickup Habilitado" : "Pickup Deshabilitado"}
                            checked={state.pickup.enabled}
                            onChange={(e) => update("pickup.enabled", e.target.checked)}
                        />
                    </Card>
                    {/* Modulo de reservas */}
                    <Card className="p-3 mb-2">
                        <strong className="mb-2 d-block">Reservas</strong>
                        <Form.Check
                            type="switch"
                            id="reservations-enabled"
                            label={state.reservations.enabled ? "Reservas Habilitadas" : "Reservas Deshabilitadas"}
                            checked={!!state.reservations?.enabled}
                            onChange={(e) => update("reservations.enabled", e.target.checked)}
                        />
                    </Card>
                    {/* Modulo de eventos */}
                    <Card className="p-3 mb-2">
                        <strong className="mb-2 d-block">Eventos</strong>

                        <Form.Check
                            type="switch"
                            id="events-enabled"
                            label={state.events?.enabled ? "Eventos Habilitados" : "Eventos Deshabilitados"}
                            checked={!!state.events?.enabled}
                            onChange={(e) => update("events.enabled", e.target.checked)}
                        />

                        <RenderIf isTrue={!!state.events?.enabled}>
                            <Row className="g-3 mt-2">
                                <Col md={6}>
                                    <Form.Check
                                        type="switch"
                                        id="events-require-access-code"
                                        label={
                                            state.events?.require_access_code
                                                ? "Requerir código de acceso"
                                                : "No requerir código de acceso"
                                        }
                                        checked={!!state.events?.require_access_code}
                                        onChange={(e) => update("events.require_access_code", e.target.checked)}
                                    />
                                    <Form.Text className="text-muted d-block">
                                        Si está activo, el usuario deberá ingresar un código para acceder al evento o continuar el flujo.
                                    </Form.Text>
                                </Col>

                                <Col md={6}>
                                    <Form.Check
                                        type="switch"
                                        id="events-require-stand"
                                        label={
                                            state.events?.require_stand
                                                ? "Obligar ingreso de stand"
                                                : "Stand opcional"
                                        }
                                        checked={!!state.events?.require_stand}
                                        onChange={(e) => update("events.require_stand", e.target.checked)}
                                    />
                                    <Form.Text className="text-muted d-block">
                                        Si está activo, el usuario deberá indicar su stand antes de continuar.
                                    </Form.Text>
                                </Col>
                            </Row>
                        </RenderIf>
                    </Card>
                    {/* Modulo de cotizaciones */}
                    <Card className="p-3 mb-2">
                        <div className="d-flex justify-content-between align-items-center">
                            <strong>Cotizaciones</strong>
                            <Form.Check
                                type="switch"
                                id="quotes-enabled"
                                label={state.quotes?.enabled ? "Cotización Habilitada" : "Cotización Deshabilitada"}
                                checked={!!state.quotes?.enabled}
                                onChange={(e) => update("quotes.enabled", e.target.checked)}
                            />
                        </div>

                        <Form.Text className="d-block">
                            Cuando está habilitado, el ecommerce deja de ser checkout y se comporta como cotizador.
                        </Form.Text>

                        <RenderIf isTrue={!!state.quotes?.enabled}>
                            <Row className="g-3 mt-2">
                                <Col md={4}>
                                    <Form.Label>Modo</Form.Label>
                                    <Form.Select
                                        value={state.quotes?.mode || "catalog"}
                                        onChange={(e) => update("quotes.mode", e.target.value)}
                                    >
                                        <option value="catalog">Catálogo (carrito → solicitar cotización)</option>
                                        <option value="direct">Directo (1 botón → cotizar)</option>
                                    </Form.Select>
                                </Col>

                                <Col md={4} className="d-flex align-items-end">
                                    <Form.Check
                                        type="switch"
                                        id="quotes-show-prices"
                                        label={state.quotes?.allow_price_visibility ? "Precios visibles" : "Precios ocultos"}
                                        checked={!!state.quotes?.allow_price_visibility}
                                        onChange={(e) => update("quotes.allow_price_visibility", e.target.checked)}
                                    />
                                </Col>

                                <Col md={4} className="d-flex align-items-end">
                                    <Form.Check
                                        type="switch"
                                        id="quotes-require-contact"
                                        label={state.quotes?.require_contact ? "Requiere datos de contacto" : "Contacto opcional"}
                                        checked={!!state.quotes?.require_contact}
                                        onChange={(e) => update("quotes.require_contact", e.target.checked)}
                                    />
                                </Col>

                                <Col md={12}>
                                    <strong className="d-block mb-1">Canales</strong>
                                    <Row className="g-2">
                                        <Col md={3}>
                                            <Form.Check
                                                type="switch"
                                                id="quotes-channel-whatsapp"
                                                label={state.quotes?.channels?.whatsapp ? "WhatsApp habilitado" : "WhatsApp deshabilitado"}
                                                checked={!!state.quotes?.channels?.whatsapp}
                                                onChange={(e) => update("quotes.channels.whatsapp", e.target.checked)}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <Form.Check
                                                type="switch"
                                                id="quotes-channel-email"
                                                label={state.quotes?.channels?.email ? "Email habilitado" : "Email deshabilitado"}
                                                checked={!!state.quotes?.channels?.email}
                                                onChange={(e) => update("quotes.channels.email", e.target.checked)}
                                            />
                                        </Col>
                                    </Row>

                                    <Form.Text className="text-muted d-block mt-1">
                                        Si deshabilitas ambos canales, el usuario no tendrá forma de enviar la cotización.
                                    </Form.Text>
                                </Col>
                            </Row>
                        </RenderIf>
                    </Card>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleSave}>Guardar</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
// ——— Modal: HEAD / SEO ———
function HeadSeoModal({ value, onChange }) {
    const [show, setShow] = useState(false);
    const [state, setState] = useState(mapHeadFromServer(value));

    const hydrate = React.useCallback(() => {
        setState(mapHeadFromServer(value));
    }, [value]);

    useEffect(() => {
        if (!show) hydrate();
    }, [value, show, hydrate]);

    const open = () => { hydrate(); setShow(true); };

    const update = (path, v) => {
        setState((s) => {
            const clone = structuredClone(s);
            const keys = path.split(".");
            let cur = clone;
            for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
            cur[keys.at(-1)] = v;
            return clone;
        });
    };

    const handleSave = () => {
        if (!state.title?.trim()) return ErrorAlert("El título es obligatorio.", "Advertencia!", "warning");
        if (!state.meta.description?.trim()) return ErrorAlert("La meta descripción es obligatoria.", "Advertencia!", "warning");

        // Normaliza salida para el backend.
        onChange({
            title: state.title.trim(),
            meta: { description: state.meta.description.trim() },
            icon: {
                file: state.icon.file || null,
                url: state.icon.file ? "" : (state.icon.existingUrl || ""),
                public_id: state.icon.public_id || "",
            },
            social: {
                // Usamos og_* (el backend puede mapear a "og:title" si quiere)
                og_title: state.social.og_title?.trim() || "",
                og_description: state.social.og_description?.trim() || "",
                og_site_name: state.social.og_site_name?.trim() || "",
                og_locale: state.social.og_locale?.trim() || "es_PA",
            },
        });

        setShow(false);
        SuccessAlert("Head/SEO actualizado", "OK", "success");
    };

    return (
        <>
            <Button variant="outline-primary" onClick={open} className="ms-2">SEO / Head</Button>

            <Modal show={show} onHide={() => setShow(false)} size="lg" centered>
                <Modal.Header closeButton><Modal.Title>SEO / Head (simple)</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Card className="p-3 mb-3">
                        <strong className="mb-2 d-block">Básico</strong>
                        <Form.Label>Título</Form.Label>
                        <Form.Control
                            value={state.title}
                            onChange={(e) => update("title", e.target.value)}
                            placeholder='Smart eCommerce | Tecnología y Accesorios en Panamá'
                        />

                        <Form.Label className="mt-2">Meta Descripción</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={state.meta.description}
                            onChange={(e) => update("meta.description", e.target.value)}
                            placeholder="Compra tecnología, gadgets y accesorios con entregas rápidas en Panamá..."
                        />
                    </Card>

                    <Card className="p-3 mb-3">
                        <strong className="mb-2 d-block">Icono del sitio (favicon / app icon)</strong>
                        <Row className="g-3">
                            <Col md={6}>
                                <FileInputWithPreview
                                    value={state.icon.file}
                                    existingUrl={state.icon.existingUrl}
                                    onChange={(f) => update("icon.file", f)}
                                    accept="image/*"
                                    label="Icono (imagen)"
                                />
                            </Col>
                            <Col md={6} className="d-flex align-items-end">
                                <Form.Text className="text-muted">
                                    Sube un PNG o ICO. Tamaños comunes: 32x32, 180x180. El backend guardará la URL final.
                                </Form.Text>
                            </Col>
                        </Row>
                    </Card>

                    <Card className="p-3">
                        <strong className="mb-2 d-block">Open Graph</strong>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Label>og:title</Form.Label>
                                <Form.Control
                                    value={state.social.og_title}
                                    onChange={(e) => update("social.og_title", e.target.value)}
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label>og:site_name</Form.Label>
                                <Form.Control
                                    value={state.social.og_site_name}
                                    onChange={(e) => update("social.og_site_name", e.target.value)}
                                />
                            </Col>
                            <Col md={12}>
                                <Form.Label>og:description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={state.social.og_description}
                                    onChange={(e) => update("social.og_description", e.target.value)}
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label>og:locale</Form.Label>
                                <Form.Control
                                    value={state.social.og_locale}
                                    onChange={(e) => update("social.og_locale", e.target.value)}
                                    placeholder="es_PA"
                                />
                            </Col>
                        </Row>
                    </Card>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleSave}>Guardar</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}


// ——— Modal: ORDER EMAIL NOTIFICATIONS ———
// Manages email recipients for order notifications (TO/CC/BCC). 
function OrderEmailNotificationsModal({ value, onChange }) {
    const [show, setShow] = React.useState(false);

    const defaults = {
        enabled: false,
        to: [],   // array of emails
        cc: [],   // array of emails
        bcc: [],  // array of emails
    };

    const [state, setState] = React.useState(value || defaults);
    const [errors, setErrors] = React.useState({ to: "", cc: "", bcc: "" });

    // Basic email validator (good-enough for UI; backend must re-validate)
    const isValidEmail = (e) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((e || "").trim().toLowerCase());

    // Split textarea into emails by comma, semicolon, whitespace or new lines
    const splitEmails = (str) =>
        (str || "")
            .split(/[,\s;]+/g)
            .map((x) => x.trim().toLowerCase())
            .filter(Boolean);

    // Join arrays for UI textareas
    const joinEmails = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");

    const hydrate = React.useCallback(() => {
        setState({
            ...defaults,
            ...(value || {}),
            to: Array.isArray(value?.to) ? value.to : [],
            cc: Array.isArray(value?.cc) ? value.cc : [],
            bcc: Array.isArray(value?.bcc) ? value.bcc : [],
        });
        setErrors({ to: "", cc: "", bcc: "" });
    }, [value]);

    React.useEffect(() => {
        if (!show) hydrate();
    }, [value, show, hydrate]);

    const open = () => {
        hydrate();
        setShow(true);
    };

    const validateArray = (label, arr) => {
        if (!Array.isArray(arr)) return `${label}: invalid value`;
        // Allow empty arrays (optional)
        const bad = arr.filter((e) => !isValidEmail(e));
        if (bad.length) return `${label}: invalid email(s): ${bad.slice(0, 3).join(", ")}${bad.length > 3 ? "..." : ""}`;
        // Deduplicate
        const dedup = [...new Set(arr)];
        if (dedup.length !== arr.length) return `${label}: duplicate emails removed automatically`;
        return "";
    };

    const handleSave = () => {
        const next = { ...state };
        // Validate and set errors if needed
        const errs = {
            to: validateArray("TO", next.to),
            cc: validateArray("CC", next.cc),
            bcc: validateArray("BCC", next.bcc),
        };
        setErrors(errs);

        // If any "invalid" error exists (not dedup notice), block save
        const hasBlocking =
            [errs.to, errs.cc, errs.bcc].some((msg) => msg && !msg.includes("duplicate emails removed"));

        if (hasBlocking) return ErrorAlert("Revisa los correos inválidos en TO/CC/BCC.", "Advertencia!", "warning");

        // Auto-deduplicate before saving
        next.to = [...new Set(next.to)];
        next.cc = [...new Set(next.cc)];
        next.bcc = [...new Set(next.bcc)];

        // At least one recipient when enabled
        if (next.enabled && next.to.length + next.cc.length + next.bcc.length === 0) {
            return ErrorAlert("Habilitado requiere al menos un correo en TO, CC o BCC.", "Advertencia!", "warning");
        }

        onChange(next);
        setShow(false);
        SuccessAlert("Notificaciones guardadas", "OK", "success");
    };

    // Handlers for textareas -> arrays
    const setList = (field, raw) => {
        const arr = splitEmails(raw);
        setState((s) => ({ ...s, [field]: arr }));
    };

    return (
        <>
            <Button variant="outline-secondary" onClick={open} className="ms-2">
                Notificaciones por correo
            </Button>

            <Modal show={show} onHide={() => setShow(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Notificaciones de Órdenes por Correo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Card className="p-3 mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <strong>Estado</strong>
                            <Form.Check
                                type="switch"
                                id="notify-enabled"
                                label={state.enabled ? "Habilitado" : "Deshabilitado"}
                                checked={state.enabled}
                                onChange={(e) => setState((s) => ({ ...s, enabled: e.target.checked }))}
                            />
                        </div>
                        <Form.Text>
                            Cuando esté habilitado, al crear una orden se enviará un correo a los destinatarios configurados.
                        </Form.Text>
                    </Card>

                    <Row className="g-3">
                        <Col md={12}>
                            <Form.Label>TO (destinatarios principales)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                disabled={!state.enabled}
                                placeholder="correo1@empresa.com, correo2@empresa.com"
                                defaultValue={joinEmails(state.to)}
                                onChange={(e) => setList("to", e.target.value)}
                            />
                            {errors.to && <div className="small mt-1 text-warning">{errors.to}</div>}
                            <Form.Text className="d-block">Separar por coma, punto y coma o salto de línea.</Form.Text>
                        </Col>

                        <Col md={6}>
                            <Form.Label>CC (con copia)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                disabled={!state.enabled}
                                placeholder="cc@empresa.com, soporte@empresa.com"
                                defaultValue={joinEmails(state.cc)}
                                onChange={(e) => setList("cc", e.target.value)}
                            />
                            {errors.cc && <div className="small mt-1 text-warning">{errors.cc}</div>}
                        </Col>

                        <Col md={6}>
                            <Form.Label>BCC (copia oculta)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                disabled={!state.enabled}
                                placeholder="auditoria@empresa.com"
                                defaultValue={joinEmails(state.bcc)}
                                onChange={(e) => setList("bcc", e.target.value)}
                            />
                            {errors.bcc && <div className="small mt-1 text-warning">{errors.bcc}</div>}
                        </Col>
                    </Row>

                    <Card className="p-3 mt-3">
                        <strong>Notas</strong>
                        <ul className="mb-0">
                            <li>El backend debe enviar el correo al crear la orden si <code>enabled === true</code>.</li>
                            <li>Los emails se guardan como arrays (<code>to</code>, <code>cc</code>, <code>bcc</code>) para tu plantilla de envío.</li>
                        </ul>
                    </Card>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleSave}>Guardar</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

// ——— Modal: APPROVAL RULES / ORDER APPROVAL FLOW ———
// This modal controls whether an order needs manual approval before confirming the purchase.
// Two modes:
//   - "always": all orders require approval
//   - "conditional": only some orders trigger approval (based on product rules)
function ApprovalRulesModal({ value, onChange }) {
    const [show, setShow] = React.useState(false);

    // Default shape for approval config
    const defaults = {
        enabled: false,          // global switch (if false -> never require approval)
        mode: "always",          // "always" | "conditional"
        rules: [
            // {
            //   product_id: "",        // selected product/group item id from menu
            //   display_name: "",      // cached label so UI can show it nicely after reload
            //   min_qty: 1,
            //   require_if_present: true,
            //   note: ""
            // }
        ]
    };

    // Local state for approval logic
    const [state, setState] = React.useState(value || defaults);

    // Local state for product options (for the select)
    // We will keep raw list from API and a version for rendering
    const [menuItems, setMenuItems] = React.useState([]);
    // menuItems will keep combined order: separators + products

    const [loadingProducts, setLoadingProducts] = React.useState(false);

    // --- Load products & separators from API ---
    // NOTE: selectedBranch is fixed to 0 for now (global products)
    const loadProducts = React.useCallback(async () => {
        try {
            setLoadingProducts(true);

            const token = localStorage.getItem("x-access-token");
            const resp = await GroupsFechtAPI.getGroupsByBranchoffice(0, token);

            const data = resp?.data?.data || [];


            // Filter products (normal / reservable)
            const combined = data.map(item => {
                if (item.type !== "separator") {
                    return {
                        id: item.group_id,                       // unique id from backend
                        name: item.name || item.title || item.label || `Item ${item.id}`,
                        type: item.type,                   // "separator" | "normal" | "reservable" | etc.
                        // keep any other field you may want later
                    };
                } else {
                    return null; // skip separators for now
                }
            }).filter(item => item !== null); // remove NULL (non-product/non-separator) items

            setMenuItems(combined);
        } catch (err) {
            // If request fails, fallback to empty
            setMenuItems([]);
        } finally {
            setLoadingProducts(false);
        }
    }, []);

    // hydrate when opening / external prop changes
    const hydrate = React.useCallback(() => {
        setState({
            ...defaults,
            ...(value || {}),
            rules: Array.isArray(value?.rules) && value.rules.length
                ? value.rules.map(r => ({
                    product_id: r.product_id || "",
                    display_name: r.display_name || "",
                    min_qty: Number(r.min_qty ?? 1),
                    require_if_present: !!r.require_if_present,
                    note: r.note || ""
                }))
                : []
        });
    }, [value]);

    // when modal visibility changes OR parent value changes
    React.useEffect(() => {
        if (!show) {
            hydrate();
        }
    }, [value, show, hydrate]);

    // load products only when modal is opened
    const open = () => {
        hydrate();
        loadProducts();
        setShow(true);
    };

    // --- Helpers to mutate local state ---

    // Generic deep update for root-level fields
    const updateRoot = (path, v) => {
        setState(prev => {
            const clone = structuredClone(prev);
            const keys = path.split(".");
            let cur = clone;
            for (let i = 0; i < keys.length - 1; i++) {
                cur = cur[keys[i]];
            }
            cur[keys.at(-1)] = v;
            return clone;
        });
    };

    const addRule = () => {
        setState(prev => ({
            ...prev,
            rules: [
                ...prev.rules,
                {
                    product_id: "",
                    display_name: "",
                    min_qty: 1,
                    require_if_present: true,
                    note: ""
                }
            ]
        }));
    };

    // When user changes a rule field
    const setRuleField = (idx, field, v) => {
        setState(prev => {
            const clone = structuredClone(prev);
            if (!clone.rules[idx]) return clone;

            if (field === "min_qty") {
                clone.rules[idx][field] = Number(v || 1);
            } else {
                clone.rules[idx][field] = v;
            }

            return clone;
        });
    };

    // Special handler for product select, because we also want to store the label
    const setRuleProduct = (idx, productId) => {
        // Find the selected item info in menuItems
        const selectedItem = menuItems.find(m => String(m.id) === String(productId));
        const label = selectedItem ? selectedItem.name : "";

        setState(prev => {
            const clone = structuredClone(prev);
            if (!clone.rules[idx]) return clone;
            clone.rules[idx].product_id = productId;
            clone.rules[idx].display_name = label;
            return clone;
        });
    };

    const removeRule = (idx) => {
        setState(prev => ({
            ...prev,
            rules: prev.rules.filter((_, i) => i !== idx)
        }));
    };

    // --- Validation before saving to parent ---
    const handleSave = () => {
        // If disabled, no problem
        if (state.enabled && state.mode === "conditional") {
            // must have at least one valid rule
            if (!state.rules.length) {
                return ErrorAlert(
                    "Agrega al menos una regla o cambia el modo a 'Siempre requiere aprobación'.",
                    "Advertencia!",
                    "warning"
                );
            }

            for (const r of state.rules) {
                // product is required
                if (!r.product_id) {
                    return ErrorAlert(
                        "Cada regla debe seleccionar un producto.",
                        "Advertencia!",
                        "warning"
                    );
                }

                // If not require_if_present, then min_qty must be >=1
                if (!r.require_if_present && (!r.min_qty || r.min_qty < 1)) {
                    return ErrorAlert(
                        "Cantidad mínima inválida en una de las reglas.",
                        "Advertencia!",
                        "warning"
                    );
                }
            }
        }

        // Build clean object for parent
        onChange({
            enabled: !!state.enabled,
            mode: state.mode || "always",
            rules: state.rules.map(r => ({
                product_id: r.product_id,
                display_name: r.display_name || "",
                min_qty: Number(r.min_qty || 1),
                require_if_present: !!r.require_if_present,
                note: r.note?.trim() || ""
            }))
        });

        setShow(false);
        SuccessAlert("Reglas de aprobación guardadas", "OK", "success");
    };

    // --- Render helpers ---

    // We'll render the select options in the same order as `menuItems`.
    // "separator" rows will be disabled and styled like a visual group.
    const renderProductSelect = (rule, idx) => {
        return (
            <Form.Group className="mb-2">
                <Form.Label>Producto</Form.Label>
                <Form.Select
                    value={rule.product_id || ""}
                    disabled={!state.enabled}
                    onChange={(e) => setRuleProduct(idx, e.target.value)}
                >
                    <option value="">Seleccione un producto…</option>
                    {menuItems.map((item, i) => {
                        const isSeparator = item.type === "separator";
                        return (
                            <option
                                key={`item-${i}`}
                                value={isSeparator ? "" : item.id}
                                disabled={isSeparator}
                                style={isSeparator ? {
                                    fontWeight: "bold",
                                    backgroundColor: "#f0f0f0",
                                } : {}}
                            >
                                {isSeparator
                                    ? `── ${item.name || "Sección"} ──`
                                    : `${item.name || `Producto ${item.id}`}`}
                            </option>
                        );
                    })}
                </Form.Select>
                <Form.Text className="text-muted">
                    Si seleccionas un separador (bloque), no se va a guardar. Debe ser un producto válido.
                </Form.Text>
            </Form.Group>
        );
    };

    return (
        <>
            <Button
                variant="outline-danger"
                onClick={open}
                className="ms-2"
            >
                Aprobación de pedidos
            </Button>

            <Modal show={show} onHide={() => setShow(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Flujo de aprobación de pedidos</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Card className="p-3 mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <strong>Estado general</strong>
                            <Form.Check
                                type="switch"
                                id="approval-enabled"
                                label={state.enabled ? "Habilitado" : "Deshabilitado"}
                                checked={state.enabled}
                                onChange={(e) => updateRoot("enabled", e.target.checked)}
                            />
                        </div>
                        <Form.Text className="d-block">
                            Si está deshabilitado, ningún pedido pide aprobación.
                        </Form.Text>
                    </Card>

                    <Card className="p-3 mb-3">
                        <strong className="mb-2 d-block">Modo de aprobación</strong>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Check
                                    type="radio"
                                    name="approval-mode"
                                    id="approval-always"
                                    label="Siempre requiere aprobación"
                                    checked={state.mode === "always"}
                                    onChange={() => updateRoot("mode", "always")}
                                    disabled={!state.enabled}
                                />
                                <Form.Text className="text-muted">
                                    Todo pedido debe ser aprobado manualmente antes de confirmarse.
                                </Form.Text>
                            </Col>
                            <Col md={6}>
                                <Form.Check
                                    type="radio"
                                    name="approval-mode"
                                    id="approval-conditional"
                                    label="Condicional por productos"
                                    checked={state.mode === "conditional"}
                                    onChange={() => updateRoot("mode", "conditional")}
                                    disabled={!state.enabled}
                                />
                                <Form.Text className="text-muted">
                                    Solo dispara aprobación si detecta ciertos productos / cantidades.
                                </Form.Text>
                            </Col>
                        </Row>
                    </Card>

                    <RenderIf isTrue={state.enabled && state.mode === "conditional"}>
                        <Card className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex flex-column">
                                    <strong>Reglas por producto</strong>
                                    {loadingProducts && (
                                        <span className="small text-muted">Cargando productos…</span>
                                    )}
                                </div>

                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={addRule}
                                    disabled={loadingProducts || !state.enabled}
                                >
                                    Agregar Regla
                                </Button>
                            </div>

                            {state.rules.length === 0 && (
                                <div className="text-muted small mt-2">
                                    No hay reglas aún. Agrega al menos una.
                                </div>
                            )}

                            {state.rules.map((rule, idx) => (
                                <Card className="p-3 mt-3" key={`approval-rule-${idx}`}>
                                    <Row className="g-3">

                                        <Col md={4}>
                                            {renderProductSelect(rule, idx)}
                                            <Form.Text className="text-muted">
                                                Guardamos también el nombre para mostrarlo luego.
                                            </Form.Text>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Label>Cant. mínima</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    min={1}
                                                    value={rule.min_qty}
                                                    disabled={!state.enabled || rule.require_if_present}
                                                    onChange={(e) => setRuleField(idx, "min_qty", e.target.value)}
                                                />
                                                <InputGroup.Text>unid.</InputGroup.Text>
                                            </InputGroup>
                                            <Form.Text className="text-muted">
                                                Se exige aprobación si el cliente compra al menos esta cantidad.
                                            </Form.Text>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Label>¿Solo con que aparezca?</Form.Label>
                                            <Form.Check
                                                type="switch"
                                                id={`approval-presence-${idx}`}
                                                label={rule.require_if_present ? "Sí, con 1 ya basta" : "No, usar cantidad"}
                                                checked={rule.require_if_present}
                                                disabled={!state.enabled}
                                                onChange={(e) => setRuleField(idx, "require_if_present", e.target.checked)}
                                            />
                                            <Form.Text className="text-muted">
                                                Si está activo, ignora la cantidad mínima.
                                            </Form.Text>
                                        </Col>

                                        <Col md={12}>
                                            <Form.Label>Nota interna (opcional)</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={rule.note}
                                                disabled={!state.enabled}
                                                onChange={(e) => setRuleField(idx, "note", e.target.value)}
                                                placeholder="Motivo de aprobación: producto controlado, límite de venta, requiere verificación de pago, etc."
                                            />
                                        </Col>

                                        <Col md={12} className="d-flex justify-content-end">
                                            <Button
                                                variant="outline-danger"
                                                onClick={() => removeRule(idx)}
                                                disabled={!state.enabled}
                                            >
                                                Eliminar regla
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card>
                            ))}
                        </Card>
                    </RenderIf>
                </Modal.Body>

                <Modal.Footer>
                    <Button onClick={handleSave}>Guardar</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}




// ——— Componente principal ———
export default function EcommerceManager() {
    const [heroSection, setHeroSection] = useState({ type: "image", media: [{ file: null, title: "", subtitle: "" }] });
    const [benefitCards, setBenefitCards] = useState([]);
    const [banners, setBanners] = useState([]);
    const [contactPage, setContactPage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasExisting, setHasExisting] = useState(false);
    const [head, setHead] = useState(mapHeadFromServer());

    // config UI
    const [uiConfig, setUiConfig] = useState({
        theme: "light",
        fontFamily: "Sans serif",
        googleFont: "",
        categories: { enabled: false, layout: "grid", items: [] },
        recommended: { enabled: false, source: "bestsellers", limit: 8, items: [] },
        delivery: { enabled: false },
        pickup: { enabled: false },
        reservations: {
            enabled: false,
        },
        events: {
            enabled: false,
            require_access_code: false,
            require_stand: false,
        },
        // Quotes module config
        quotes: {
            enabled: false,
            mode: "catalog",
            allow_price_visibility: false,
            require_contact: true,
            channels: { whatsapp: true, email: true }
        }
    });
    // approval / manual order validation config
    const [approvalConfig, setApprovalConfig] = useState({
        enabled: false,
        mode: "always", // "always" | "conditional"
        rules: []        // [{ product_id, display_name, min_qty, require_if_present, note }]
    });

    // email notifications config (order emails)
    const [orderEmailNotify, setOrderEmailNotify] = useState({
        enabled: false,
        to: [],
        cc: [],
        bcc: [],
    });

    const resolvedTheme = (uiConfig.theme === "auto"
        ? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? "dark" : "light")
        : uiConfig.theme);

    // Aplica tema (data-theme en <body>) y fuente (inyecta link de Google font)
    useEffect(() => {
        // tema
        const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        const resolved = uiConfig.theme === "auto" ? (prefersDark ? "dark" : "light") : uiConfig.theme;
        document.body.setAttribute("data-theme", resolved);

        // fuente
        const id = "ecom-google-font";
        const prev = document.getElementById(id);
        if (prev) prev.remove();

        if (uiConfig.googleFont?.trim()) {
            const link = document.createElement("link");
            link.id = id;
            link.rel = "stylesheet";
            const family = uiConfig.googleFont.trim().replace(/ /g, "+");
            // peso regular + semibold para títulos
            link.href = `https://fonts.googleapis.com/css2?family=${family}:wght@400;600&display=swap`;
            document.head.appendChild(link);
        }
    }, [uiConfig.theme, uiConfig.googleFont]);

    // We charge existing content on mount (if any)
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                LoadingAlert("Cargando contenido existente...", "Cargando", "info");

                const businessId = JSON.parse(localStorage.getItem('user')).business.id;
                const token = localStorage.getItem("x-access-token");
                // API : GET /ecommerce/:business_id/content/published
                const res = await EcommerceManagerFetchAPI.getContent(businessId, token);

                if (res?.data?.data) {
                    const d = res.data.data;
                    // d.hero_section, d.benefit_cards, d.banners, d.contact_page 
                    setHeroSection(mapHeroFromServer(d.hero_section));
                    setBenefitCards(mapBenefitsFromServer(d.benefit_cards));
                    setBanners(mapBannersFromServer(d.banners));
                    setContactPage(d.contact_page || null);
                    setUiConfig(d.ui_config || uiConfig);
                    setHead(mapHeadFromServer(d.head));

                    // hydrate approvalConfig if present in backend
                    const _approval = d?.approval_config;
                    setApprovalConfig({
                        enabled: !!_approval?.enabled,
                        mode: _approval?.mode || "always",
                        rules: Array.isArray(_approval?.rules)
                            ? _approval.rules.map(r => ({
                                product_id: r.product_id || "",
                                display_name: r.display_name || "",
                                min_qty: Number(r.min_qty ?? 1),
                                require_if_present: !!r.require_if_present,
                                note: r.note || ""
                            }))
                            : []
                    });

                    //hydrate notifications if present
                    const _cfg = d?.notifications?.order_email;
                    setOrderEmailNotify({
                        enabled: !!_cfg?.enabled,
                        to: Array.isArray(_cfg?.to) ? _cfg.to : [],
                        cc: Array.isArray(_cfg?.cc) ? _cfg.cc : [],
                        bcc: Array.isArray(_cfg?.bcc) ? _cfg.bcc : [],
                    });
                    SuccessAlert("Contenido precargado", "OK", "success");
                    setHasExisting(true);
                } else {
                    // No hay contenido; dejar estados por defecto
                    setHasExisting(false);
                }
            } catch (e) {
                ErrorAlert("No se pudo cargar contenido existente", "Ups", "warning");
                setHasExisting(false);
            } finally {
                setLoading(false);

            }
        })();
    }, []);

    //Function to save all content
    const handleSaveAll = async () => {
        if (!preValidate()) return;
        try {
            setLoading(true);
            LoadingAlert("Guardando contenido…", "Cargando", "info");
            const fd = buildFormData(false); // create
            const businessId = JSON.parse(localStorage.getItem('user')).business.id;
            const token = localStorage.getItem("x-access-token");
            await EcommerceManagerFetchAPI.postContent(businessId, fd, token);
            setHasExisting(true);
            SuccessAlert("Contenido creado", "Éxito", "success");
        } catch (e) {
            ErrorAlert("Error al guardar: " + e.message, "Error", "danger");
        } finally {
            setLoading(false);
        }
    };
    const buildFormData = (isUpdate = false) => {
        const fd = new FormData();

        // to indicate existing media; 
        const payload = {
            hero_section: {
                type: heroSection.type,
                media: heroSection.media.map((m) => ({
                    url: m.file ? "" : (m.existingUrl || ""),
                    public_id: m.public_id || "",
                    title: m.title || "",
                    subtitle: m.subtitle || "",
                })),
            },
            benefit_cards: benefitCards.map((c) => ({
                url: c.file ? "" : (c.existingUrl || ""),
                public_id: c.public_id || "",
                title: c.title || "",
                description: c.description || "",
            })),
            banners: banners.map((b) => ({
                url: b.file ? "" : (b.existingUrl || ""),
                public_id: b.public_id || "",
                title: b.title || "",
                description: b.description || "",
                type: b.type,
                position: String(b.position ?? ""),
                button: b.cta_text?.trim()
                    ? { text: b.cta_text.trim(), href: b.cta_url?.trim() || "#" }
                    : undefined,
            })),
            contact_page: contactPage || undefined,
            ui_config: uiConfig,
            notifications: {
                order_email: {
                    enabled: !!orderEmailNotify.enabled,
                    to: Array.isArray(orderEmailNotify.to) ? orderEmailNotify.to : [],
                    cc: Array.isArray(orderEmailNotify.cc) ? orderEmailNotify.cc : [],
                    bcc: Array.isArray(orderEmailNotify.bcc) ? orderEmailNotify.bcc : [],
                },
            },
            head: {
                title: head.title,
                meta: { description: head.meta.description },
                icon: {
                    url: head.icon.file ? "" : (head.icon.existingUrl || ""),
                    public_id: head.icon.public_id || "",
                },
                social: {
                    og_title: head.social.og_title,
                    og_description: head.social.og_description,
                    og_site_name: head.social.og_site_name,
                    og_locale: head.social.og_locale,
                },
            },
            approval_config: {
                enabled: !!approvalConfig.enabled,
                mode: approvalConfig.mode || "always",
                rules: Array.isArray(approvalConfig.rules)
                    ? approvalConfig.rules.map(r => ({
                        product_id: r.product_id,
                        display_name: r.display_name || "",
                        min_qty: Number(r.min_qty || 1),
                        require_if_present: !!r.require_if_present,
                        note: r.note || ""
                    }))
                    : []
            },
        };

        fd.append("payload", JSON.stringify(payload));
        fd.append("slug", "home");
        /**
         * Add file to FormData if it's a new file (instanceof File)
         * - Post (create): all files are new
         * - Put (update): only new files (not existing URLs)
         */
        const appendIfNeeded = (hasFile) => hasFile instanceof File;

        // Icono
        if (appendIfNeeded(head.icon.file)) {
            fd.append("files.icon", head.icon.file);
        }

        //Hero
        heroSection.media.forEach((m, i) => {
            if (!isUpdate) {
                if (m.file instanceof File) fd.append(`files.hero.${i}`, m.file);
            } else {
                // update: solo nuevos
                if (appendIfNeeded(m.file)) fd.append(`files.hero.${i}`, m.file);
            }
        });

        benefitCards.forEach((c, i) => {
            if (!isUpdate) {
                if (c.file instanceof File) fd.append(`files.benefits.${i}`, c.file);
            } else {
                if (appendIfNeeded(c.file)) fd.append(`files.benefits.${i}`, c.file);
            }
        });

        banners.forEach((b, i) => {
            if (!isUpdate) {
                if (b.file instanceof File) fd.append(`files.banners.${i}`, b.file);
            } else {
                if (appendIfNeeded(b.file)) fd.append(`files.banners.${i}`, b.file);
            }
        });

        return fd;
    };

    const preValidate = () => {
        if (!contactPage) { ErrorAlert("Completa la sección de Contacto.", "Advertencia!", "warning"); return false; }
        if (!heroSection.media?.length) { ErrorAlert("Configura la sección Hero.", "Advertencia!", "warning"); return false; }
        return true;
    };

    //Function to update the content in the ecommerce manager
    const handleUpdateAll = async () => {
        if (!preValidate()) return;
        try {
            setLoading(true);
            LoadingAlert("Actualizando contenido…", "Cargando", "info");
            const fd = buildFormData(true); // update
            const businessId = JSON.parse(localStorage.getItem('user')).business.id;
            const token = localStorage.getItem("x-access-token");
            await EcommerceManagerFetchAPI.updateContent(businessId, fd, token);
            SuccessAlert("Contenido actualizado", "Éxito", "success");
        } catch (e) {
            ErrorAlert("Error al actualizar: " + e.message, "Error", "danger");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Navbar titlePage="Ecommerce manager" route="config/new_ecommerce" />
            <div className="container py-3">
                <Card className="p-3">
                    <div className="d-flex flex-wrap gap-2">
                        <HeroModal value={heroSection} onChange={setHeroSection} />
                        <BenefitsModal value={benefitCards} onChange={setBenefitCards} />
                        <BannersModal value={banners} onChange={setBanners} />
                        <ContactModal value={contactPage} onChange={setContactPage} />
                        <ConfigModal value={uiConfig} onChange={setUiConfig} />
                        <OrderEmailNotificationsModal value={orderEmailNotify} onChange={setOrderEmailNotify} />
                        <HeadSeoModal value={head} onChange={setHead} />
                        {/*<ApprovalRulesModal value={approvalConfig} onChange={setApprovalConfig} />*/}

                    </div>

                    <hr className="my-4" />

                    <div className="d-flex gap-2">
                        <RenderIf isTrue={!hasExisting}>
                            <Button onClick={handleSaveAll} >
                                Guardar Todo (crear)
                            </Button>
                        </RenderIf>

                        <RenderIf isTrue={hasExisting}>
                            <Button variant="primary" onClick={handleUpdateAll} >
                                Publicar
                            </Button>
                        </RenderIf>
                    </div>
                </Card>
            </div>
        </>
    );
}
